/**
 * Task Form component for creating and editing tasks
 */
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/themes.css';

const TaskForm = ({ task, onBack }) => {
  const { createTask, updateTask } = useAdmin();
  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    title_en: '',
    category: '',
    difficulty: 'easy',
    text_en: '',
    text_ru: '',
    example_en: '',
    meta_en: '',
    sample_code: '',
    url: ''
  });

  const isEditing = !!task;

  useEffect(() => {
    if (task) {
      setFormData({
        title_en: task.title_en || '',
        category: task.category || '',
        difficulty: task.difficulty || 'easy',
        text_en: task.text_en || '',
        text_ru: task.text_ru || '',
        example_en: task.example_en || '',
        meta_en: task.meta_en || '',
        sample_code: task.sample_code || '',
        url: task.url || ''
      });
    }
  }, [task]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare data, removing empty fields for optional ones
      const taskData = {
        title_en: formData.title_en,
        category: formData.category,
        difficulty: formData.difficulty,
        text_en: formData.text_en
      };

      // Add optional fields only if they have values
      if (formData.text_ru) taskData.text_ru = formData.text_ru;
      if (formData.example_en) taskData.example_en = formData.example_en;
      if (formData.meta_en) taskData.meta_en = formData.meta_en;
      if (formData.sample_code) taskData.sample_code = formData.sample_code;
      if (formData.url) taskData.url = formData.url;

      if (isEditing) {
        await updateTask(task.id, taskData);
        setSuccess('Task updated successfully');
      } else {
        await createTask(taskData);
        setSuccess('Task created successfully');
        // Reset form for new task
        setFormData({
          title_en: '',
          category: '',
          difficulty: 'easy',
          text_en: '',
          text_ru: '',
          example_en: '',
          meta_en: '',
          sample_code: '',
          url: ''
        });
      }
    } catch (err) {
      setError(`Failed to ${isEditing ? 'update' : 'create'} task: ` + err.message);
    } finally {
      setLoading(false);
    }
  };

  const difficultyOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
    { value: 'expert', label: 'Expert' }
  ];

  return (
    <div className="themed-background" style={{ padding: '1rem', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ color: 'var(--color-background-text)' }}>
          {isEditing ? 'Edit Task' : 'Create New Task'}
        </h2>
        <button
          onClick={onBack}
          className="themed-button"
          style={{ padding: '0.5rem 1rem' }}
        >
          ← Back to Tasks
        </button>
      </div>

      {/* Form */}
      <div className="themed-surface" style={{
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px var(--shadow-color)',
        maxWidth: '800px'
      }}>
        {error && (
          <div className="status-error themed-surface" style={{
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid var(--color-error)'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div className="status-success themed-surface" style={{
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid var(--color-success)'
          }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{
              marginBottom: '1rem',
              color: 'var(--color-surface-text)'
            }}>Basic Information</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--color-surface-text)'
              }}>
                Title (English) *
              </label>
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleInputChange}
                required
                placeholder="Enter task title"
                className="themed-input"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: 'var(--color-surface-text)'
                }}>
                  Category *
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., algorithms, data-structures"
                  className="themed-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: 'var(--color-surface-text)'
                }}>
                  Difficulty *
                </label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  required
                  className="themed-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem'
                  }}
                >
                  {difficultyOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Task Content */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: 'var(--color-surface-text)' }}>Task Content</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--color-surface-text)'
              }}>
                Description (English) *
              </label>
              <textarea
                name="text_en"
                value={formData.text_en}
                onChange={handleInputChange}
                required
                rows="6"
                placeholder="Enter task description..."
                className="themed-input"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--color-surface-text)'
              }}>
                Description (Russian)
              </label>
              <textarea
                name="text_ru"
                value={formData.text_ru}
                onChange={handleInputChange}
                rows="6"
                placeholder="Enter Russian description (optional)..."
                className="themed-input"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--color-surface-text)'
              }}>
                Example
              </label>
              <textarea
                name="example_en"
                value={formData.example_en}
                onChange={handleInputChange}
                rows="4"
                placeholder="Provide examples or test cases..."
                className="themed-input"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--color-surface-text)'
              }}>
                Sample Code
              </label>
              <textarea
                name="sample_code"
                value={formData.sample_code}
                onChange={handleInputChange}
                rows="8"
                placeholder="def solution():&#10;    # Your code here&#10;    pass"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Metadata */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: 'var(--color-surface-text)' }}>Metadata</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--color-surface-text)'
              }}>
                Additional Information
              </label>
              <textarea
                name="meta_en"
                value={formData.meta_en}
                onChange={handleInputChange}
                rows="3"
                placeholder="Additional metadata, hints, or notes..."
                className="themed-input"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--color-surface-text)'
              }}>
                Reference URL
              </label>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://example.com/reference"
                className="themed-input"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--border-color)'
          }}>
            <button
              type="submit"
              disabled={loading}
              className="themed-button"
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: 'var(--color-success)',
                fontSize: '1rem',
                fontWeight: 'bold',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Task' : 'Create Task')}
            </button>

            <button
              type="button"
              onClick={onBack}
              className="themed-button"
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: 'var(--color-outline)',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;